import type { $Fetch } from "nitropack";

// ---- Status string union (kept in sync with backend `LogoChangeRequestStatus`) ----

export type LogoChangeRequestStatus =
    | 'pending'
    | 'approved'
    | 'rejected';

// ---- Admin list ----
// Matches `AdminLogoRequestItem` in
// `api/crates/rest/src/v1/admin/logo_requests.rs`.
export interface AdminLogoRequestItem {
    logo_change_request_id: string;
    company_id: string;
    company_name: string | null;
    proposed_logo_url: string | null;
    status: LogoChangeRequestStatus;
    created_at: string;
}

export interface AdminLogoRequestListResponse {
    items: AdminLogoRequestItem[];
    total: number;
}

// ---- Admin detail ----
// Matches `AdminLogoRequestDetailResponse` in
// `api/crates/rest/src/v1/admin/logo_requests.rs`.
export interface AdminLogoRequestDetailResponse {
    logo_change_request_id: string;
    company_id: string;
    company_name: string | null;
    current_logo_url: string | null;
    proposed_logo_url: string | null;
    status: LogoChangeRequestStatus;
    rejection_reason: string | null;
    created_at: string;
}

// ---- Mutations ----

export interface OkResponse {
    ok: true;
}

export const LogoChangeRequestRepository = (client: $Fetch) => ({
    fetchAdminList: (params?: {
        status?: LogoChangeRequestStatus;
        limit?: number;
        offset?: number;
    }) => {
        const qs = new URLSearchParams();
        if (params?.status) qs.append('status', params.status);
        if (params?.limit !== undefined) qs.append('limit', String(params.limit));
        if (params?.offset !== undefined) qs.append('offset', String(params.offset));
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        return client<AdminLogoRequestListResponse>(`/v1/admin/logo-requests${suffix}`, {
            method: 'GET',
        });
    },

    fetchDetail: (id: string) =>
        client<AdminLogoRequestDetailResponse>(`/v1/admin/logo-requests/${id}`, {
            method: 'GET',
        }),

    approve: (id: string) =>
        client<OkResponse>(`/v1/admin/logo-requests/${id}/approve`, {
            method: 'POST',
        }),

    reject: (id: string, reason?: string) =>
        client<OkResponse>(`/v1/admin/logo-requests/${id}/reject`, {
            method: 'POST',
            body: { reason },
        }),
});
